<template>
  <div class="form">
    <p class="form__title">{{ t('forms.signUp') }}</p>
    <div class="form__inputs">
      <ui-input
        :type="'text'"
        :placeholder="t('fields.fname')"
        :rules="[required]"
        v-model="firstName"
      />
      <ui-input
        :type="'text'"
        :placeholder="t('fields.lname')"
        :rules="[required]"
        v-model="lastName"
      />
      <ui-input
        :type="'email'"
        :placeholder="t('fields.email')"
        :rules="[isEmail]"
        v-model="email"
      />
      <ui-input
        :type="'password'"
        :placeholder="t('fields.password')"
        :rules="[isPasswordValid]"
        v-model="password"
      />
      <ui-input
        :type="'password'"
        :placeholder="t('fields.passwordConfirm')"
        :rules="[compareStrings]"
        v-model="passwordConfirm"
      />
    </div>
    <ui-checkbox
        class="form__agree"
        v-model="isAgree"
    >
      <p>
        {{ t('docs.agreeText1') }}
        <router-link class="form__link" to="">{{ t('footer.nav.terms') }}</router-link>
        {{ t('docs.agreeText2') }}
        <router-link class="form__link" to="">{{ t('footer.nav.privacy') }}</router-link>
      </p>
    </ui-checkbox>
    <ui-button
      :isDisabled="!isFormValid"
      :isLoading="isLoading"
      @click="register"
    >
      {{ t('buttons.createAcc') }}
    </ui-button>
    <p class="form__login">
      {{ t('forms.haveAcc') }}
      <span @click="appStore.setActiveState('signIn')">{{ t('forms.signIn') }}</span>
    </p>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import UiInput from "@/components/ui/ui-input.vue";
import {isEmail, isPasswordValid, required} from "@/core/rules/textFieldRules.js";
import {computed, h, ref} from "vue";
import {useAppStore} from "@/stores/app.js";
import UiButton from "@/components/ui/ui-button.vue";
import UiCheckbox from "@/components/ui/ui-checkbox.vue";
import {Auth} from "@/core/api/auth.js";
import {ElNotification} from "element-plus";

const {t} = useI18n()
const appStore = useAppStore()

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const password = ref('')
const passwordConfirm = ref('')
const isAgree = ref(false)

const compareStrings = (v) => {
  if (v === password.value) return true
  return t('errors.compare')
}

const mailClient = ref(null)
const isLoading = ref(false)

const isFormValid = computed(() => {
  return (
    required(firstName.value) === true &&
    required(lastName.value) === true &&
    isEmail(email.value) === true &&
    isPasswordValid(password.value) === true &&
    compareStrings(passwordConfirm.value) === true &&
    isAgree.value
  )
})

const register = async () => {
  isLoading.value = true

  const res = await Auth.register({
    firstName: firstName.value,
    lastName: lastName.value,
    email: email.value,
    password: password.value,
    passwordConfirm: passwordConfirm.value,
  })

  if (res?.data?.createUser?.success) {
    const domain = email.value.split('@')[1]
    const mailClients = {
      'gmail.com': 'https://mail.google.com/',
      'outlook.com': 'https://outlook.live.com/',
      'icloud.com': 'https://www.icloud.com/',
      'yahoo.com': 'https://mail.yahoo.com/'
    }

    Object.entries(mailClients).forEach((el) => {
      if (domain === el[0]) mailClient.value = el[1]
    })

    ElNotification({
      message: h('div', [
        h('p', t('popup.registerSuccess')),
        mailClient ? h(
          'button',
          {
            style: {
              marginTop: '10px',
              padding: '6px 12px',
              backgroundColor: '#2f6b4f',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            },
            onClick: () => {
              window.location.href = mailClient.value
            }
          },
          t('buttons.goEmail')
        ) : ''
      ]),
      type: 'success'
    })
  } else {
    ElNotification({
      message: res.errors[0].message,
      type: 'error'
    })
  }

  appStore.setActiveState('')
  isLoading.value = false
}
</script>

<style lang="scss" scoped>
.form {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;

  &__title {
    font-family: Poppins, sans-serif;
    font-size: 30px;
    font-weight: 600;
    color: $main;
  }

  &__inputs {
    width: 100%;
    margin-top: 25px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }

  &__agree {
    align-self: flex-start;
  }

  &__link {
    transition: 0.2s;
    color: $main;

    @include hover {
      color: $mainHover;
    }
  }

  &__login {
    align-self: flex-start;
    font-family: Poppins,sans-serif;
    font-size: 14px;
    margin-top: 10px;

    & span {
      cursor: pointer;
      transition: 0.2s;
      color: $main;

      @include hover {
        color: $mainHover;
      }
    }
  }
}
</style>